import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { authApi } from "@/lib/api";
import { getAccessRequestSuccessMessage, getAuthErrorMessage } from "@/lib/authMessages";
import { AUTH_STALE_EVENT, normalizeApiError } from "@/lib/apiErrors";
import { clearSessionEndedNotice, setSessionEndedNotice } from "@/lib/sessionNotice";
import { clearPreviewSession } from "@/lib/previewMode";
import { clearUserSnapshot, readUserSnapshot, writeUserSnapshot } from "@/lib/userSnapshot";

const AuthContext = createContext(null);

function clearStoredAuth() {
  localStorage.removeItem("cognivio_token");
  clearPreviewSession();
  // W14 (C-352 §1) — CLEARS 1, 2 and 3 land here, because all three already funnel through this
  // function: a deliberate `logout`, a 401 or expiry via the auth-stale handler, and a 401 from
  // `/api/me` on load. The snapshot must never outlive the token that justified it.
  //
  // ⚑ NEW-12 (C-384 §5) NARROWED CLEAR 3, and this comment used to state the wider version: "a
  // FAILED `/api/me` on load". That was the defect in one word. Any failure included an
  // unreachable host, so a deploy ran this function and destroyed a credential the server had
  // never refused. The caller is where the test lives; the wording is corrected here because this
  // comment is what the next reader will take the rule from.
  clearUserSnapshot();
}

export function AuthProvider({ children }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("cognivio_token");

    if (!token) {
      setUser(null);
      return null;
    }

    try {
      const res = await authApi.me();
      setUser(res.data);
      // W14 — the snapshot is written ONLY from a `/api/me` projection, never from the login
      // payload, which is a different and smaller shape (LOGIN-SEAM-1 below). Writing here rather
      // than at each caller means a workspace switch, an MFA change and a plain reload all refresh
      // it by the same path they already take.
      writeUserSnapshot(res.data);
      return res.data;
    } catch (error) {
      // ⚑ NEW-12 (C-384 §5) — ONLY A 401 ENDS A SESSION. This catch called `clearStoredAuth()`
      // for EVERY rejection, so a `/api/me` that failed at network level — a deploy, a dropped
      // connection — destroyed the token in `localStorage`. Nobody was refused: the client logged
      // the reader out, and no retry recovered them because the credential was gone.
      //
      // `normalizeApiError` reports an unreachable host as `status: 0` and returns before any
      // auth reasoning, so the distinction this line needs is one the app already draws on every
      // other surface rather than a new one invented here.
      //
      // ⚑ THE STATUS IS READ, NOT `isAuthStale`, and that is deliberate: `isAuthStale` is
      // NARROWER than 401 — it excludes `invalid_credentials` and the two pending-account codes so
      // those keep their own copy — so using it here would leave a dead token in storage for
      // exactly those 401s. The ruling is "only a 401 may clear", so 401 is the test.
      //
      // A 403 and a 500 no longer clear either, which is the same rule read correctly: both mean
      // the credential was accepted and something else went wrong.
      if (normalizeApiError(error).status === 401) {
        clearStoredAuth();
      }
      // ⚑ `setUser(null)` stays unconditional, and that is a BOUNDARY rather than an oversight.
      // Keeping the W14 snapshot painted through an outage would let unvalidated stored identity
      // drive the shell for as long as the network is down; W14 ruled it may drive ONE frame,
      // ahead of an unconditional revalidation. Extending that to "for the duration of an outage"
      // is W14's ruling to extend, not this call site's — so the reader is still returned to the
      // login screen during a deploy, and is signed straight back in afterwards because the
      // credential survived. Reported to COMMAND rather than decided here.
      setUser(null);
      throw error;
    }
  }, []);

  useEffect(() => {
    let active = true;

    const initialize = async () => {
      const token = localStorage.getItem("cognivio_token");

      if (!token) {
        if (active) {
          setUser(null);
          setInitializing(false);
        }
        return;
      }

      // ⚑ W14 (C-352 §1) — THE FIRST FRAME, PAINTED BEFORE THE ROUND TRIP. With a token in hand,
      // a stored snapshot is enough to render the right shell immediately; `/api/me` still goes out
      // on the next line and replaces it. This is the whole of the 1234 -> 779.5 ms win.
      //
      // `initializing` is released here on purpose: it is what every shell waits on, so leaving it
      // true until the response arrives would keep the spinner up and buy nothing. The snapshot is
      // a PAINT and not a grant — see `userSnapshot.js` — and the revalidation below is
      // unconditional, so a snapshot that no longer matches the server is corrected rather than
      // trusted.
      const snapshot = readUserSnapshot();
      if (snapshot && active) {
        setUser(snapshot);
        setInitializing(false);
      }

      try {
        const nextUser = await refreshUser();
        if (active) {
          setUser(nextUser);
        }
      } catch {
        if (active) {
          setUser(null);
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    initialize();

    return () => {
      active = false;
    };
  }, [refreshUser]);

  useEffect(() => {
    const onAuthStale = (event) => {
      clearStoredAuth();
      setUser(null);
      queryClient.clear();
      // AUTH-WAY-1 (FO3) — persist the reason BEFORE the route guard redirects to /login, so the
      // reader arrives at a page that explains itself instead of a bare form. The toast stays for
      // the case where no navigation follows (the reader is already on a public page); the stored
      // notice is what survives the redirect that usually does. Both carry the same translated
      // sentence, resolved in apiErrors against the active language.
      const detail = event?.detail;
      setSessionEndedNotice(detail);
      const message = detail?.message;
      if (message) {
        toast.error(message);
      }
    };

    window.addEventListener(AUTH_STALE_EVENT, onAuthStale);
    return () => window.removeEventListener(AUTH_STALE_EVENT, onAuthStale);
  }, [queryClient]);

  // PRIV-2 (MFA): when a login is paused for a second factor, the response carries no token — it carries
  // a short-lived mfa_token plus a mode flag. We stash it here and the AuthPage renders the MFA step.
  const [pendingMfa, setPendingMfa] = useState(null);
  const clearPendingMfa = useCallback(() => setPendingMfa(null), []);

  /**
   * LOGIN-SEAM-1 — the client held TWO different shapes of user, and only one of them was complete.
   *
   * THE DEFECT THIS CLOSES, measured rather than supposed. The login response's user is built by
   * `auth_service._build_user_response` → `legacy.UserResponse(**public_doc)`, a Pydantic model that
   * declares its fields and sets no `extra` — so it DROPS anything not declared on it. `GET /api/me`
   * builds a different object: `self_user_view` (an allow-list) plus fields COMPUTED on top of it,
   * such as `reflective_available` and `record_live_enabled`.
   *
   * So a computed field was present after a RELOAD and absent after a LOGIN. The first consumer of
   * one found that out the hard way: with the recorder's server grant live, a teacher who signed in
   * and went straight to the capture route got a 404, because the gate reads `record_live_enabled`
   * and the login payload has no such field. Reloading fixed it, which is the signature of this
   * class of bug and the reason it survives review — everything works on the second look.
   *
   * Nothing caught it because both halves were right in isolation: the server returns the field, the
   * client reads it correctly. Nobody tested the shape of the object the client actually HOLDS in
   * the seconds after sign-in.
   *
   * THE FIX IS ONE SHAPE EVERYWHERE, not one more field. `/api/me`'s allow-list is a superset of the
   * login payload's declared fields (checked field by field against what this app reads off `user`),
   * so replacing the one with the other loses nothing and gains every computed field — including the
   * next one somebody adds, which is the half of this worth having.
   *
   * THE LOGIN PAYLOAD IS STILL SHOWN FIRST, deliberately: the UI renders immediately and the
   * projection replaces it when it arrives, so sign-in does not wait on a second round trip.
   *
   * AND IT DOES NOT USE `refreshUser`, WHICH IS THE TRAP HERE. That function clears the stored token
   * and nulls the user when `/api/me` fails — correct for a page load with a stale token, and
   * catastrophic one line after a SUCCESSFUL sign-in: a transient blip would log the teacher
   * straight back out, which is worse than the 404 this fixes. So the refresh is done directly and
   * the login payload is KEPT on failure.
   */
  const finalizeSession = useCallback(
    (data) => {
      clearPreviewSession();
      localStorage.setItem("cognivio_token", data.token);
      setUser(data.user);
      queryClient.clear();
      setPendingMfa(null);

      return authApi
        .me()
        .then((res) => {
          if (res?.data) setUser(res.data);
          // W14 — sign-in is the one path that reaches `/api/me` without going through
          // `refreshUser` (see the paragraph above on why it must not), so the snapshot is written
          // here too. Without this the very first load after a fresh sign-in would have nothing to
          // paint from, which is the load most likely to be on a phone.
          if (res?.data) writeUserSnapshot(res.data);
          return res?.data || data.user;
        })
        .catch(() => {
          // The sign-in SUCCEEDED. A failed projection fetch leaves her signed in with the login
          // payload — fewer computed fields, every field the app routes on — and never signs her out.
          return data.user;
        });
    },
    [queryClient],
  );

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (res) => {
      const data = res?.data || {};
      if (data.mfa_required) {
        setPendingMfa({ mode: "challenge", mfaToken: data.mfa_token });
        return; // no token yet — AuthPage shows the code prompt
      }
      if (data.mfa_enrollment_required) {
        setPendingMfa({ mode: "enroll", mfaToken: data.mfa_token });
        return; // no token yet — AuthPage shows forced enrollment
      }
      finalizeSession(data);
      toast.success(t("auth.loggedInSuccessfully"));
    },
    onError: (error) => {
      toast.error(getAuthErrorMessage(error, t("auth.loginFailed")));
    },
  });

  // MFA challenge: enrolled user submits a TOTP / recovery code → session.
  const mfaChallengeMutation = useMutation({
    mutationFn: (payload) => authApi.mfaChallenge({ mfa_token: pendingMfa?.mfaToken, code: payload.code }),
    onSuccess: (res) => {
      finalizeSession(res?.data || {});
      toast.success(t("auth.loggedInSuccessfully"));
    },
    onError: (error) => {
      toast.error(getAuthErrorMessage(error, t("auth.mfaCodeInvalid")));
    },
  });

  // Forced enrollment (role requires MFA, not yet enrolled): start returns secret+uri; complete enables
  // MFA AND mints the session, returning one-time recovery codes.
  const mfaForcedEnrollStartMutation = useMutation({
    mutationFn: () => authApi.mfaForcedEnrollStart({ mfa_token: pendingMfa?.mfaToken }),
    onError: (error) => toast.error(getAuthErrorMessage(error, t("auth.mfaEnrollFailed"))),
  });
  const mfaForcedEnrollCompleteMutation = useMutation({
    mutationFn: (payload) => authApi.mfaForcedEnrollComplete({ mfa_token: pendingMfa?.mfaToken, code: payload.code }),
    onSuccess: (res) => {
      finalizeSession(res?.data || {});
      toast.success(t("auth.mfaEnabled"));
    },
    onError: (error) => toast.error(getAuthErrorMessage(error, t("auth.mfaCodeInvalid"))),
  });

  // Voluntary enrollment / management (any authenticated user, from the security settings panel).
  const mfaEnrollMutation = useMutation({ mutationFn: () => authApi.mfaEnroll() });
  const mfaActivateMutation = useMutation({
    mutationFn: (payload) => authApi.mfaActivate({ code: payload.code }),
    onSuccess: () => { refreshUser().catch(() => {}); toast.success(t("auth.mfaEnabled")); },
    onError: (error) => toast.error(getAuthErrorMessage(error, t("auth.mfaCodeInvalid"))),
  });
  const mfaDisableMutation = useMutation({
    mutationFn: (payload) => authApi.mfaDisable({ code: payload.code }),
    onSuccess: () => { refreshUser().catch(() => {}); toast.success(t("auth.mfaDisabled")); },
    onError: (error) => toast.error(getAuthErrorMessage(error, t("auth.mfaCodeInvalid"))),
  });
  const mfaRegenerateMutation = useMutation({
    mutationFn: (payload) => authApi.mfaRecoveryRegenerate({ code: payload.code }),
    onSuccess: () => { refreshUser().catch(() => {}); toast.success(t("auth.mfaRecoveryRegenerated")); },
    onError: (error) => toast.error(getAuthErrorMessage(error, t("auth.mfaCodeInvalid"))),
  });

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (res) => {
      if (res?.data?.status === "pending" && !res?.data?.token) {
        toast.success(res?.data?.message || t("auth.requestAccessSubmitted"));
        return;
      }
      clearPreviewSession();
      localStorage.setItem("cognivio_token", res.data.token);
      setUser(res.data.user);
      queryClient.clear();
      toast.success(t("auth.accountCreated"));
    },
    onError: (error) => {
      toast.error(getAuthErrorMessage(error, t("auth.registrationFailed")));
    },
  });

  const requestAccessMutation = useMutation({
    mutationFn: authApi.requestAccess,
    onSuccess: (res) => {
      toast.success(getAccessRequestSuccessMessage(res?.data, t("auth.requestAccessSubmitted")));
    },
    onError: (error) => {
      toast.error(getAuthErrorMessage(error, t("auth.requestAccessFailed")));
    },
  });

  const requestPasswordResetMutation = useMutation({
    mutationFn: authApi.requestPasswordReset,
    onSuccess: (res) => {
      toast.success(res?.data?.message || t("auth.passwordResetRequestSubmitted"));
    },
    onError: (error) => {
      toast.error(getAuthErrorMessage(error, t("auth.passwordResetRequestFailed")));
    },
  });

  const confirmPasswordResetMutation = useMutation({
    mutationFn: authApi.confirmPasswordReset,
    onSuccess: (res) => {
      toast.success(res?.data?.message || t("auth.passwordResetConfirmSuccess"));
    },
    onError: (error) => {
      toast.error(getAuthErrorMessage(error, t("auth.passwordResetConfirmFailed")));
    },
  });

  const logout = useCallback(() => {
    authApi.logout().catch(() => {});
    clearStoredAuth();
    // AUTH-WAY-1 (FO3) — a deliberate sign-out is not an expiry. Without this, a logout that
    // happened to follow a stale request would greet the reader with "your session expired",
    // which is both wrong and faintly alarming.
    clearSessionEndedNotice();
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo(
    () => ({
      user,
      initializing,
      loading: initializing,
      isLoading: initializing,
      isAuthenticated: Boolean(user),
      login: (payload) => loginMutation.mutate(payload),
      loginAsync: (payload) => loginMutation.mutateAsync(payload),
      // PRIV-2 MFA
      pendingMfa,
      clearPendingMfa,
      mfaChallengeAsync: (payload) => mfaChallengeMutation.mutateAsync(payload),
      mfaForcedEnrollStartAsync: () => mfaForcedEnrollStartMutation.mutateAsync(),
      mfaForcedEnrollCompleteAsync: (payload) => mfaForcedEnrollCompleteMutation.mutateAsync(payload),
      mfaEnrollAsync: () => mfaEnrollMutation.mutateAsync(),
      mfaActivateAsync: (payload) => mfaActivateMutation.mutateAsync(payload),
      mfaStatusAsync: () => authApi.mfaStatus().then((r) => r.data),
      mfaDisableAsync: (payload) => mfaDisableMutation.mutateAsync(payload),
      mfaRegenerateAsync: (payload) => mfaRegenerateMutation.mutateAsync(payload),
      mfaChallenging: mfaChallengeMutation.isPending,
      mfaEnrolling: mfaForcedEnrollCompleteMutation.isPending || mfaActivateMutation.isPending,
      mfaManaging: mfaEnrollMutation.isPending || mfaActivateMutation.isPending
        || mfaDisableMutation.isPending || mfaRegenerateMutation.isPending,
      register: (payload) => registerMutation.mutate(payload),
      registerAsync: (payload) => registerMutation.mutateAsync(payload),
      requestAccess: (payload) => requestAccessMutation.mutate(payload),
      requestAccessAsync: (payload) => requestAccessMutation.mutateAsync(payload),
      requestPasswordReset: (payload) => requestPasswordResetMutation.mutate(payload),
      requestPasswordResetAsync: (payload) => requestPasswordResetMutation.mutateAsync(payload),
      confirmPasswordReset: (payload) => confirmPasswordResetMutation.mutate(payload),
      confirmPasswordResetAsync: (payload) => confirmPasswordResetMutation.mutateAsync(payload),
      loggingIn: loginMutation.isPending,
      registering: registerMutation.isPending,
      requestingAccess: requestAccessMutation.isPending,
      requestingPasswordReset: requestPasswordResetMutation.isPending,
      confirmingPasswordReset: confirmPasswordResetMutation.isPending,
      logout,
      refreshUser,
      setUserProfile: (nextUser) => setUser(nextUser),
    }),
    [
      user,
      initializing,
      loginMutation,
      registerMutation,
      requestAccessMutation,
      requestPasswordResetMutation,
      confirmPasswordResetMutation,
      logout,
      refreshUser,
      pendingMfa,
      clearPendingMfa,
      mfaChallengeMutation,
      mfaForcedEnrollStartMutation,
      mfaForcedEnrollCompleteMutation,
      mfaEnrollMutation,
      mfaActivateMutation,
      mfaDisableMutation,
      mfaRegenerateMutation,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return ctx;
}

/**
 * NAV-SWEEP-1b — the session, or null, WITHOUT throwing.
 *
 * `useAuth` throwing outside a provider is the right default: anything that gates on identity must
 * fail loudly rather than silently treat a missing session as "logged out". This variant exists for
 * the narrow opposite case — DECORATIVE copy that reads the session to personalise a sentence and
 * already ships a designed fallback for the value (the admin-viewer banner's {{org}}). There, a
 * missing provider should render the fallback phrasing, not crash a page.
 *
 * Do NOT use this for access decisions. If the answer changes what a user can DO, use useAuth.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
